/**
 * @file AnalyticsRangeBar.tsx
 * @brief Date range + group-by control bar shared across Analytics
 *        dashboards and report shells.
 *
 * @details
 * Flush `GridCells` layout (29-07-2026): quick presets, custom range,
 * group-by, and an optional trailing cell (e.g. company picker).
 * Fully controlled — parent owns state. ISO strings for `startDate` /
 * `endDate` (start-of-day / end-of-day) so Postgres comparisons are inclusive.
 *
 * Group-by (30-09-2026): no dropdown. DES: "you dont need day dropdown
 * here because we have this in range picker anyway". The range already
 * says how fine the chart can be, so the bar picks it: up to a month by
 * Day, up to six months by Week, longer (or no range) by Month.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — end-to-end GridCell filters
 * @updated 30-09-2026 — group-by follows the range; dropdown removed
 */

import { useEffect, type ReactNode } from "react";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import {
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { cn } from "@/shared/theme/cn";
import type { GroupBy } from "../schemas";

export const flatAnalyticsFilterControlClass =
  "h-11 w-full rounded-none border-0 bg-transparent px-4 shadow-none ring-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0";

export interface AnalyticsRange {
  startDate?: string;
  endDate?: string;
  groupBy: GroupBy;
}

interface AnalyticsRangeBarProps {
  value: AnalyticsRange;
  onChange: (next: AnalyticsRange) => void;
  /**
   * Optional trailing cell(s) — e.g. company filter — rendered inside
   * the same `GridCells` row as the range controls.
   */
  trailing?: ReactNode;
  /** @deprecated Kept for call-site compat; always flush GridCells now. */
  variant?: "card" | "cell";
}

// ----------------------------------------------------------------------
// Preset helpers — all return ISO strings (UTC) so the backend date
// comparisons are unambiguous.
// ----------------------------------------------------------------------
function isoStartOfDay(d: Date): string {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c.toISOString();
}
function isoEndOfDay(d: Date): string {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c.toISOString();
}

function presetRange(days: number | "ytd"): {
  startDate: string;
  endDate: string;
} {
  const end = new Date();
  const start = new Date();
  if (days === "ytd") {
    start.setMonth(0);
    start.setDate(1);
  } else {
    start.setDate(end.getDate() - days);
  }
  return { startDate: isoStartOfDay(start), endDate: isoEndOfDay(end) };
}

const PRESETS: {
  id: string;
  label: string;
  build: () => { startDate: string; endDate: string };
}[] = [
  { id: "7d", label: "7d", build: () => presetRange(7) },
  { id: "30d", label: "30d", build: () => presetRange(30) },
  { id: "90d", label: "90d", build: () => presetRange(90) },
  { id: "ytd", label: "YTD", build: () => presetRange("ytd") },
];

/** How finely a range is charted: Day to a month, Week to six months, else Month. */
export function groupByForRange(startDate?: string, endDate?: string): GroupBy {
  if (!startDate || !endDate) return "month";
  const days =
    (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000;
  if (!Number.isFinite(days)) return "month";
  if (days <= 32) return "day";
  if (days <= 184) return "week";
  return "month";
}

export function AnalyticsRangeBar({
  value,
  onChange,
  trailing,
}: AnalyticsRangeBarProps) {
  // Every change carries the grouping its range implies.
  const emit = (next: AnalyticsRange) =>
    onChange({ ...next, groupBy: groupByForRange(next.startDate, next.endDate) });

  // A page's starting range may carry another grouping; bring it in line once.
  const derived = groupByForRange(value.startDate, value.endDate);
  useEffect(() => {
    if (value.groupBy !== derived) onChange({ ...value, groupBy: derived });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derived, value.groupBy]);

  const setPreset = (p: (typeof PRESETS)[number]) => {
    const r = p.build();
    emit({ ...value, ...r });
  };

  const isPresetActive = (p: (typeof PRESETS)[number]) => {
    const r = p.build();
    return r.startDate === value.startDate && r.endDate === value.endDate;
  };

  return (
    <GridCells
      className={cn(
        "grid-cols-1 sm:grid-cols-2",
        trailing ? "xl:grid-cols-3" : "xl:grid-cols-2",
      )}
    >
      <GridCell padded={false}>
        <div className="flex h-11" role="group" aria-label="Quick range">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPreset(p)}
              className={cn(
                "flex-1 rounded-none border-0 text-sm font-medium transition-colors",
                isPresetActive(p)
                  ? "bg-brand-soft text-brand"
                  : "bg-transparent text-muted-foreground hover:bg-secondary/40 hover:text-foreground",
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </GridCell>

      <GridCell padded={false}>
        <DateRangePicker
          from={value.startDate}
          to={value.endDate}
          onChange={(next) =>
            emit({
              ...value,
              startDate: next.from,
              endDate: next.to,
            })
          }
          size="sm"
          placeholder="Custom range"
          className={flatAnalyticsFilterControlClass}
        />
      </GridCell>

      {trailing}
    </GridCells>
  );
}
